import { Button } from "@/components/ui/button";
import { SimpleTooltip } from "@/components/ui/simple-tooltip";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Combobox } from "@/components/ui/combobox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FancyMultiSelect } from "@/components/ui/fancy-multi-select";
import { X } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { dateQueryPreview, needsDatePreview } from "../../lib/date-query-preview";
import { cn, getRelativeDateExamples, getViewportBottomInset } from "../../lib/utils";
import { DRQBuilder } from "@/utils/drq";
import { Filter } from "../layout/types";
import { FieldPicker } from "./filter-tree/field-picker";
import { linkedFilterFromPath } from "./filter-tree/linked-filter";
import { toast } from "sonner";
import { formatPercentageInputFromRatio, parsePercentageInputToRatio } from "@/utils/format-time";

/**
 * Where a `position: fixed` element really starts: the viewport, unless an ancestor creates a
 * containing block for it (a transform, as a centred dialog has, a filter, perspective…). Its
 * `top` / `left` are then measured from that ancestor, not from the window.
 */
function fixedContainingBlockOrigin(element: HTMLElement): { top: number; left: number } {
    for (let node = element.parentElement; node && node !== document.body; node = node.parentElement) {
        const style = window.getComputedStyle(node);
        if (
            (style.transform && style.transform !== "none") ||
            (style.filter && style.filter !== "none") ||
            (style.perspective && style.perspective !== "none") ||
            /paint|layout|strict|content/.test(style.contain || "") ||
            /transform|perspective|filter/.test(style.willChange || "")
        ) {
            const rect = node.getBoundingClientRect();
            return { top: rect.top, left: rect.left };
        }
    }
    return { top: 0, left: 0 };
}

interface AddFilterComponentProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    availableFilters: Filter[];
    onAddFilter?: (filter: { field: string; operator: string; value: string }) => void;
    editMode?: boolean;
    initialField?: string;
    initialOperator?: string;
    initialValue?: string;
    onEditFilter?: (filter: { field: string; operator: string; value: string }) => void;
    anchorEl?: HTMLElement | null;
    /** "app_label.model_name" of the list: offers the fields of the objects it links to. */
    rootModel?: string;
    /** A field of a linked object was picked: its definition, to add to `availableFilters`. */
    onLinkedFilter?: (filter: Filter) => void;
}

function ClampedFieldDescription({ description }: { description: string }) {
    const descriptionRef = useRef<HTMLParagraphElement>(null);
    const [isTruncated, setIsTruncated] = useState(false);

    const updateTruncation = useCallback(() => {
        const element = descriptionRef.current;
        setIsTruncated(!!element && element.scrollHeight > element.clientHeight);
    }, []);

    useLayoutEffect(() => {
        updateTruncation();

        const element = descriptionRef.current;
        if (!element || typeof ResizeObserver === "undefined") return;

        const observer = new ResizeObserver(updateTruncation);
        observer.observe(element);
        return () => observer.disconnect();
    }, [description, updateTruncation]);

    const content = (
        <p ref={descriptionRef} tabIndex={isTruncated ? 0 : undefined} className="line-clamp-2 text-xs leading-snug text-gray-500">
            {description}
        </p>
    );

    if (!isTruncated) return content;

    return (
        <SimpleTooltip message={description} asChild contentClassName="max-w-sm whitespace-normal break-words">
            {content}
        </SimpleTooltip>
    );
}

// Définition des opérateurs disponibles selon le type de champ
const getOperatorsForType = (type: string) => {
    switch (type) {
        case "number":
        case "percentage":
            return [
                { value: "equals", label: "equals" },
                { value: "not_equal_to", label: "not equal to" },
                { value: "gt", label: "greater than" },
                { value: "gte", label: "greater or equal" },
                { value: "lt", label: "less than" },
                { value: "lte", label: "less or equal" },
                { value: "is_empty", label: "is empty" }
            ];
        case "text":
            return [
                { value: "contains", label: "contains" },
                { value: "not_contains", label: "not contains" },
                { value: "equals", label: "equals" },
                { value: "not_equal_to", label: "not equal to" },
                { value: "starts_with", label: "starts with" },
                { value: "ends_with", label: "ends with" },
                { value: "is_empty", label: "is empty" }
            ];
        case "select":
        case "combobox":
            return [
                { value: "equals", label: "equals" },
                { value: "not_equal_to", label: "not equal to" },
                { value: "contains", label: "contains" },
                { value: "not_contains", label: "not contains" },
                { value: "starts_with", label: "starts with" },
                { value: "ends_with", label: "ends with" },
                { value: "is_empty", label: "is empty" }
            ];
        case "checkbox":
            return [{ value: "equals", label: "equals" }];
        case "date-range":
            return [
                { value: "equals", label: "equals" },
                { value: "not_equal_to", label: "not equal to" },
                { value: "gt", label: "greater than" },
                { value: "gte", label: "greater or equal" },
                { value: "lt", label: "less than" },
                { value: "lte", label: "less or equal" },
                { value: "is_empty", label: "is empty" }
            ];
        case "datetime-range":
            return [
                { value: "equals", label: "equals" },
                { value: "not_equal_to", label: "not equal to" },
                { value: "gt", label: "greater than" },
                { value: "gte", label: "greater or equal" },
                { value: "lt", label: "less than" },
                { value: "lte", label: "less or equal" },
                { value: "is_empty", label: "is empty" }
            ];
        default:
            return [
                { value: "equals", label: "equals" },
                { value: "not_equal_to", label: "not equal to" },
                { value: "is_empty", label: "is empty" }
            ];
    }
};

// is_empty stores the boolean token "True" / "False", not a field value.
// "True" is what the query builder turns into field__isnull=true.
export function validateFilterValue({
    hasField,
    hasFilter,
    type,
    operator,
    value
}: {
    hasField: boolean;
    hasFilter: boolean;
    type: string;
    operator: string;
    value: string;
}): { isValid: boolean; errorMessage?: string } {
    const currentValue = value.trim();

    if (!hasField || !hasFilter) {
        return { isValid: true };
    }

    if (!operator && type !== "checkbox") {
        return { isValid: true };
    }

    if (operator === "is_empty" || operator === "is_not_empty") {
        if (currentValue === "True" || currentValue === "False") {
            return { isValid: true };
        }
        return { isValid: false, errorMessage: "Please select TRUE or FALSE" };
    }

    switch (type) {
        case "checkbox":
            if (!currentValue) {
                return { isValid: false, errorMessage: "Please select TRUE or FALSE" };
            }
            break;
        case "number":
        case "percentage":
            if (currentValue && isNaN(Number(currentValue))) {
                return { isValid: false, errorMessage: "Please enter a valid number" };
            }
            break;
        case "date-range":
        case "datetime-range": {
            const temporal = DRQBuilder.validateTemporalFilterInput(currentValue, operator, type);
            if (!temporal.valid) {
                return { isValid: false, errorMessage: temporal.error || "Invalid date value" };
            }
            break;
        }
    }

    return { isValid: true };
}

/** A relative date input that shows, inside its right edge, the dates the expression means right now. */
function RelativeDateInput({ operator, value, onChange, disabled, placeholder }: { operator: string; value: string; onChange: (value: string) => void; disabled?: boolean; placeholder: string }) {
    const resolved = needsDatePreview([value]) ? dateQueryPreview(operator, [value]) : null;
    return (
        <div className="relative">
            <Input disabled={disabled} placeholder={placeholder} value={value} onChange={e => onChange(e.target.value)} maxLength={50} className={cn(resolved && "pr-44")} />
            {resolved && (
                <span className="pointer-events-none absolute inset-y-0 right-3 flex max-w-40 items-center truncate text-xs tabular-nums text-muted-foreground" title={resolved}>
                    {resolved}
                </span>
            )}
        </div>
    );
}

export function AddFilterComponent({
    open: openProp,
    setOpen: setOpenProp,
    availableFilters,
    onAddFilter,
    editMode,
    initialField,
    initialOperator,
    initialValue,
    onEditFilter,
    anchorEl,
    rootModel,
    onLinkedFilter
}: AddFilterComponentProps) {
    const [selectedField, setSelectedField] = useState<string>("");
    const [selectedOperator, setSelectedOperator] = useState<string>("");
    const [filterValue, setFilterValue] = useState<string>("");
    const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
    const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);
    const popupRef = useRef<HTMLDivElement>(null);

    const updatePosition = useCallback(() => {
        const popup = popupRef.current;
        if (!anchorEl || !popup) return;

        const rect = anchorEl.getBoundingClientRect();
        const gap = 8;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const popupWidth = popup.offsetWidth || 384; // w-96
        // scrollHeight keeps the natural height even once maxHeight starts clamping the box.
        const popupHeight = Math.max(popup.scrollHeight, popup.offsetHeight);

        let left = rect.left;
        if (left + popupWidth > viewportWidth - gap) {
            left = rect.right - popupWidth;
        }
        left = Math.min(Math.max(gap, left), Math.max(gap, viewportWidth - popupWidth - gap));

        // The fixed UtilityBar overlays the bottom of the viewport on list pages, so the usable
        // area stops above it.
        const usableTop = gap;
        const usableBottom = viewportHeight - getViewportBottomInset() - gap;
        const spaceBelow = usableBottom - (rect.bottom + gap);
        const spaceAbove = rect.top - gap - usableTop;

        let top: number;
        let nextMaxHeight: number | undefined;

        if (popupHeight <= spaceBelow) {
            top = rect.bottom + gap;
        } else if (popupHeight <= spaceAbove) {
            top = rect.top - popupHeight - gap;
        } else if (popupHeight <= usableBottom - usableTop) {
            // Neither side fits: sit as close to the anchor as full visibility allows, overlapping
            // it if needed. Showing the whole popup beats staying adjacent behind a scrollbar.
            top = usableBottom - popupHeight;
        } else {
            // Taller than the usable area, so no placement can show it whole: take all of it and
            // scroll inside.
            top = usableTop;
            nextMaxHeight = usableBottom - usableTop;
        }

        // Computed in viewport coordinates; inside a dialog the popup is placed from the dialog's box.
        const origin = fixedContainingBlockOrigin(popup);
        top -= origin.top;
        left -= origin.left;

        setPosition(previous => (previous.top === top && previous.left === left ? previous : { top, left }));
        setMaxHeight(previous => (previous === nextMaxHeight ? previous : nextMaxHeight));
    }, [anchorEl]);

    useLayoutEffect(() => {
        if (!anchorEl || !openProp) return;

        updatePosition();

        // The popup grows as operator and value sections appear, and the filter panel it is
        // anchored to scrolls, so the position has to be recomputed rather than set once.
        const observer = new ResizeObserver(updatePosition);
        if (popupRef.current) observer.observe(popupRef.current);
        window.addEventListener("resize", updatePosition);
        window.addEventListener("scroll", updatePosition, true);

        return () => {
            observer.disconnect();
            window.removeEventListener("resize", updatePosition);
            window.removeEventListener("scroll", updatePosition, true);
        };
    }, [anchorEl, openProp, updatePosition]);

    // Close panel on outside click or Escape key
    useEffect(() => {
        if (!openProp) return;

        const handleMouseDown = (e: MouseEvent) => {
            const target = e.target as Element;
            if (popupRef.current && !popupRef.current.contains(target)) {
                // Radix Select/Combobox render their dropdowns in a portal outside popupRef;
                // ignore clicks inside those portals so selection does not close the panel.
                if (target.closest?.("[data-radix-popper-content-wrapper]")) {
                    return;
                }
                setOpenProp(false);
            }
        };
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                setOpenProp(false);
            }
        };

        document.addEventListener("mousedown", handleMouseDown);
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("mousedown", handleMouseDown);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [openProp, setOpenProp]);

    // Initialize form values when in edit mode
    useEffect(() => {
        if (editMode && initialField && initialOperator && initialValue !== undefined) {
            setSelectedField(initialField);
            setSelectedOperator(initialOperator);
            const filterDefinition = availableFilters.find(f => f.field_name === initialField);
            if (filterDefinition?.type === "percentage" && initialValue !== "") {
                setFilterValue(formatPercentageInputFromRatio(Number(initialValue)));
            } else {
                setFilterValue(initialValue);
            }
        } else if (!editMode) {
            // Reset form when not in edit mode
            setSelectedField("");
            setSelectedOperator("");
            setFilterValue("");
        }
    }, [editMode, initialField, initialOperator, initialValue, availableFilters]);

    // A field of a linked object is selected once it is in `availableFilters`: adding it re-runs
    // the reset above, which would clear it.
    const pendingLinkedField = useRef<string | null>(null);
    useEffect(() => {
        const field = pendingLinkedField.current;
        if (field && availableFilters.some(f => f.field_name === field)) {
            pendingLinkedField.current = null;
            setSelectedField(field);
        }
    }, [availableFilters]);

    const selectedFilter = availableFilters.find(f => f.field_name === selectedField);
    const availableOperators = selectedFilter ? getOperatorsForType(selectedFilter.type || "text") : [];

    const fieldOptions = (
        editMode && initialField
            ? [...availableFilters, { field_name: initialField, name: initialField }].filter((filter, index, self) => self.findIndex(f => f.field_name === filter.field_name) === index)
            : availableFilters
    )
        .sort((a, b) => (a.name || a.field_name).localeCompare(b.name || b.field_name))
        .map(filter => ({
            value: filter.field_name,
            label: filter.name || filter.field_name
        }));

    const validateValue = (): { isValid: boolean; errorMessage?: string } => {
        const selectedFilter = availableFilters.find(f => f.field_name === selectedField);
        return validateFilterValue({
            hasField: Boolean(selectedField),
            hasFilter: Boolean(selectedFilter),
            type: selectedFilter?.type || "text",
            operator: selectedOperator,
            value: filterValue
        });
    };

    const validation = validateValue();

    const handleAddFilter = () => {
        const validation = validateValue();
        if (!validation.isValid) {
            return;
        }

        let finalValue = filterValue;
        if (selectedFilter?.type === "percentage" && finalValue.trim() !== "") {
            const ratioValue = parsePercentageInputToRatio(finalValue);
            if (ratioValue !== undefined) {
                finalValue = String(ratioValue);
            }
        }

        // Create filter data with parsed values for backend
        const filterData = {
            field: selectedField,
            operator: selectedFilter?.type === "checkbox" ? "equals" : selectedOperator,
            value: finalValue
        };

        // Call the appropriate callback based on mode
        if (editMode && onEditFilter) {
            onEditFilter(filterData);
        } else if (!editMode && onAddFilter) {
            onAddFilter(filterData);
        }

        // Reset form only if not in edit mode
        if (!editMode) {
            setSelectedField("");
            setSelectedOperator("");
            setFilterValue("");
        }
        setOpenProp(false);
    };

    const renderValueInput = (disabled: boolean) => {
        if (!selectedFilter) return null;

        const type = selectedFilter.type || "text";

        if (selectedOperator === "is_empty" || selectedOperator === "is_not_empty") {
            return (
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={() => setFilterValue("True")}
                        disabled={disabled}
                        className={`flex-1 rounded px-3 py-2 text-sm font-medium transition-colors ${
                            filterValue === "True" ? "bg-green-500 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        } ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
                    >
                        TRUE
                    </button>
                    <button
                        type="button"
                        onClick={() => setFilterValue("False")}
                        disabled={disabled}
                        className={`flex-1 rounded px-3 py-2 text-sm font-medium transition-colors ${
                            filterValue === "False" ? "bg-red-500 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        } ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
                    >
                        FALSE
                    </button>
                </div>
            );
        }

        switch (type) {
            case "number":
            case "percentage":
            case "text":
                return <Input disabled={disabled} placeholder="Enter a value..." value={filterValue} onChange={e => setFilterValue(e.target.value)} maxLength={255} />;

            case "select":
            case "combobox":
                if (["equals", "not_equal_to"].includes(selectedOperator)) {
                    if (selectedFilter.options) {
                        // Use FancyMultiSelect for multiple selection
                        return (
                            <FancyMultiSelect
                                options={selectedFilter.options.map(option => ({
                                    value: option.value,
                                    label: option.label || option.value
                                }))}
                                value={filterValue}
                                onValueChange={setFilterValue}
                                placeholder="Select values..."
                                disabled={disabled}
                            />
                        );
                    } else if (selectedFilter.optionsUrl) {
                        return <FancyMultiSelect optionsUrl={selectedFilter.optionsUrl} value={filterValue} onValueChange={setFilterValue} placeholder="Select values..." disabled={disabled} />;
                    }
                }
                // No predefined options, use text input
                return <Input disabled={disabled} placeholder="Enter a value..." value={filterValue} onChange={e => setFilterValue(e.target.value)} maxLength={255} />;

            case "date-range":
                return (
                    <div className="space-y-2">
                        <RelativeDateInput
                            disabled={disabled}
                            placeholder="Ex: 15/01/2024, today, yesterday, last 7 days, next 10 days..."
                            operator={selectedOperator}
                            value={filterValue}
                            onChange={setFilterValue}
                        />
                        <div className="text-xs text-gray-500">
                            <p className="mb-1">Enter a date or relative expression. Examples:</p>
                            <div className="mb-1 flex flex-wrap gap-1">
                                {getRelativeDateExamples()
                                    .slice(0, 11)
                                    .map((example: string) => (
                                        <button
                                            key={example}
                                            type="button"
                                            onClick={() => {
                                                if (!disabled) {
                                                    setFilterValue(example);
                                                }
                                            }}
                                            className="cursor-pointer rounded bg-gray-100 px-1 py-0.5 text-xs hover:bg-gray-200"
                                            disabled={disabled}
                                        >
                                            {example}
                                        </button>
                                    ))}
                            </div>
                            <p className="text-xs">Supported formats: 15/01/2024, 2024-01-15, DD/MM/YYYY</p>
                        </div>
                    </div>
                );

            case "datetime-range":
                return (
                    <div className="space-y-2">
                        <RelativeDateInput
                            disabled={disabled}
                            placeholder="Ex: 15/01/2024 14:30, today, last 7 days, next 10 days..."
                            operator={selectedOperator}
                            value={filterValue}
                            onChange={setFilterValue}
                        />
                        <div className="text-xs text-gray-500">
                            <p className="mb-1">Enter a datetime or relative expression. Examples:</p>
                            <div className="mb-1 flex flex-wrap gap-1">
                                {getRelativeDateExamples()
                                    .slice(0, 11)
                                    .map((example: string) => (
                                        <button
                                            key={example}
                                            type="button"
                                            onClick={() => {
                                                if (!disabled) {
                                                    setFilterValue(example);
                                                }
                                            }}
                                            className="cursor-pointer rounded bg-gray-100 px-1 py-0.5 text-xs hover:bg-gray-200"
                                            disabled={disabled}
                                        >
                                            {example}
                                        </button>
                                    ))}
                            </div>
                            <p className="text-xs">Supported formats: 15/01/2024 14:30, 2024-01-15 14:30, 15/01/2024 (defaults to 00:00)</p>
                        </div>
                    </div>
                );

            case "checkbox":
                return (
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => setFilterValue("True")}
                            disabled={disabled}
                            className={`flex-1 rounded px-3 py-2 text-sm font-medium transition-colors ${
                                filterValue === "True" ? "bg-green-500 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            } ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
                        >
                            TRUE
                        </button>
                        <button
                            type="button"
                            onClick={() => setFilterValue("False")}
                            disabled={disabled}
                            className={`flex-1 rounded px-3 py-2 text-sm font-medium transition-colors ${
                                filterValue === "False" ? "bg-red-500 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            } ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
                        >
                            FALSE
                        </button>
                    </div>
                );

            default:
                return <Input disabled={disabled} placeholder="Enter a value..." value={filterValue} onChange={e => setFilterValue(e.target.value)} />;
        }
    };

    return (
        openProp && (
            <div
                ref={popupRef}
                className="fixed z-50 min-h-64 w-96 items-start rounded border bg-white p-4 shadow-xl"
                style={{ top: position.top, left: position.left, maxHeight, overflowY: maxHeight ? "auto" : undefined }}
            >
                <X
                    className="absolute right-2 top-2 cursor-pointer rounded p-0.5 hover:bg-gray-100"
                    size={20}
                    onClick={e => {
                        e.stopPropagation();
                        setOpenProp(false);
                    }}
                />

                <div className="space-y-4">
                    {/* Dropdown pour le champ */}
                    <div className="space-y-2">
                        <Label className="text-sm font-medium">Field</Label>
                        {rootModel && onLinkedFilter ? (
                            // The fields of the object and of the objects it links to (Registration › Email).
                            <FieldPicker
                                rootModel={rootModel}
                                labels={selectedField ? (selectedFilter?.name || selectedField).split(" › ") : []}
                                allowRelated={false}
                                className="h-9 w-full"
                                extraRootFields={availableFilters.filter(f => !f.field_name.includes("__")).map(f => ({ name: f.field_name, label: f.name || f.field_name }))}
                                onPick={async pick => {
                                    // A field of the list keeps its own definition (operators, values).
                                    if (availableFilters.some(f => f.field_name === pick.path)) {
                                        setSelectedField(pick.path);
                                        return;
                                    }
                                    const filter = await linkedFilterFromPath(rootModel, pick.path).catch(() => null);
                                    if (!filter) {
                                        toast.error("This field can only be filtered in the advanced filters");
                                        return;
                                    }
                                    pendingLinkedField.current = filter.field_name;
                                    onLinkedFilter(filter);
                                }}
                            />
                        ) : (
                            <Combobox
                                name="field"
                                options={fieldOptions}
                                value={selectedField}
                                onValueChange={value => value && setSelectedField(value)}
                                placeholder="Select a field"
                                commandPlaceholder="Search a field..."
                                className="w-full"
                            />
                        )}
                        {selectedFilter?.description?.trim() && <ClampedFieldDescription description={selectedFilter.description.trim()} />}
                    </div>

                    {/* Dropdown pour l'opérateur */}
                    {selectedFilter?.type !== "checkbox" && (
                        <div className="space-y-2">
                            <Label className="text-sm font-medium">Operator</Label>
                            <Select value={selectedOperator} onValueChange={setSelectedOperator} disabled={!selectedField}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select an operator" />
                                </SelectTrigger>
                                <SelectContent>
                                    {availableOperators
                                        .sort((a, b) => a.label.localeCompare(b.label))
                                        .map(operator => (
                                            <SelectItem key={operator.value} value={operator.value}>
                                                {operator.label}
                                            </SelectItem>
                                        ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}

                    {/* Input pour la valeur */}
                    {selectedField && (selectedOperator || selectedFilter?.type === "checkbox") && (
                        <div className="space-y-2">
                            <Label className="text-sm font-medium">Value</Label>
                            {renderValueInput(false)}
                            {!validation.isValid && validation.errorMessage && <p className="text-xs text-red-500">{validation.errorMessage}</p>}
                        </div>
                    )}

                    {/* Boutons d'action */}
                    {selectedField && (selectedOperator || selectedFilter?.type === "checkbox") && (
                        <div className="flex justify-end gap-2 pt-4">
                            <Button variant="outline" onClick={() => setOpenProp(false)}>
                                Cancel
                            </Button>
                            <Button
                                className="rounded bg-blue-500 px-3 py-2 text-sm text-white hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
                                onClick={handleAddFilter}
                                disabled={!validation.isValid}
                            >
                                {editMode ? "Update" : "Add"}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        )
    );
}
